/**
 * Workflow engine — the observed state model.
 *
 * Client-safe: types plus a handful of pure readers. `state-loader.ts` fills
 * these shapes from the database; the evaluator only ever reads them through
 * the typed key unions below, which is what keeps a catalog typo a compile
 * error rather than a step that silently never completes.
 */

import type { PropertyOnboardingState } from "../services/onboarding-steps";
import type { SubjectType } from "../types/property";
import type { WorkflowEventKey } from "./types";

// ── Events ─────────────────────────────────────────────────────────────────────

export interface WorkflowEvent {
	count: number;
	/** ISO-8601 of the most recent occurrence, or null when count is 0. */
	latestAt: string | null;
}

export type WorkflowEvents = Partial<Record<WorkflowEventKey, WorkflowEvent>>;

// ── Property state ─────────────────────────────────────────────────────────────

export interface WorkflowKeyMessageCounts {
	positive: number;
	negative: number;
	neutral: number;
	total: number;
	tags: string[];
}

export interface WorkflowProjectTypeCounts {
	SEO: number;
	PR: number;
	GENERAL: number;
}

export interface WorkflowGoalTypeCounts {
	PAGE_GROUP: number;
	PUBLISHER: number;
	ARTICLE: number;
	AUTHOR: number;
	COVERAGE_GROUP: number;
	PITCH_LIST: number;
}

export interface WorkflowPlacementCounts {
	prospective: number;
	retrospective: number;
	completed: number;
}

export interface WorkflowPipelineCounts {
	page: number;
	url: number;
	draft: number;
	/** Runs whose `querySource` is a real tracked fan-out rather than free text. */
	tracked: number;
	completed: number;
	/** Runs created to re-score a revision (`parentRunId` set). */
	rescored: number;
	latestScore: number | null;
}

export interface WorkflowAuditState {
	completedCount: number;
	latest: {
		id: string;
		completedAt: string | null;
		score: number | null;
	} | null;
}

export interface WorkflowTrafficControlState {
	blockAiModelTrainers: boolean;
	customBlockCount: number;
	customAllowCount: number;
}

export interface WorkflowLocationChild {
	id: string;
	/** How the UI names the market when it asks which one a run is about. */
	label: string;
	competitorCount: number;
}

export interface WorkflowPropertyState extends PropertyOnboardingState {
	id: string;
	subjectType: SubjectType;

	// Reports
	completedReportCount: number;
	reportSchedule: string | null;
	hasReportSchedule: boolean;
	/** Addresses the scheduled report would reach today (see Alerting). */
	reportRecipientCount: number;

	// Prompts
	activePromptCount: number;
	/** Distinct funnel stages the active prompts span (1..3). */
	promptStageCount: number;
	brandPromptCount: number;
	promptTags: string[];

	// Messaging
	keyMessages: WorkflowKeyMessageCounts;

	// Projects
	projectTypes: WorkflowProjectTypeCounts;
	completedProjectCount: number;
	goalTypes: WorkflowGoalTypeCounts;
	/**
	 * Goals of any type sitting on an ACTIVE project. `goalTypes` counts goals
	 * across every project regardless of status, which is what the playbooks
	 * that ask "is there a publisher goal anywhere" want; this one answers
	 * "is the work that is running now pointed at something".
	 */
	activeProjectGoalCount: number;

	// Timeline
	manualAnnotationCount: number;

	// Earned media
	coverageGroupCount: number;
	coverageGroupsWithScoredPlacements: number;
	coverageGroupItemsWithPlacement: number;
	placements: WorkflowPlacementCounts;
	/** Outlets on the property's own pitch lists (org-level lists excluded). */
	pitchListItemCount: number;

	// Content
	pageGroupCount: number;
	faqGenerationCount: number;

	// Locations
	locationChildren: WorkflowLocationChild[];
	locationChildCount: number;

	// Identity
	hasSnapshot: boolean;
	snapshotOverrideFieldCount: number;
	aliasCount: number;
	additionalDomainCount: number;
	suppressedDomainCount: number;

	// Alerting. Like `reportRecipientCount`, these count the addresses the
	// notification would reach today (property audience minus the people who
	// opted out), not a stored list. Everyone in the org is the default.
	wrongDomainAlertRecipientCount: number;
	negativeMessageAlertRecipientCount: number;

	// Traffic
	analyticsPlatforms: string[];
	trafficControl: WorkflowTrafficControlState;

	// Tools
	audits: WorkflowAuditState;
	pipeline: WorkflowPipelineCounts;

	// Measurement
	hasNightlyMetrics: boolean;
	hasLocationMetrics: boolean;
	consultationCount: number;

	events: WorkflowEvents;
}

// ── Organization state ─────────────────────────────────────────────────────────

export interface WorkflowOrganizationState {
	id: string;
	propertyCount: number;
	prospectCount: number;
	prospectReportsCompleteCount: number;
	prospectReportsExportedCount: number;
	/** Active agency plan, not a trial — the same gate the prospects API uses. */
	canProspect: boolean;
	hasWhiteLabelBranding: boolean;
}

// ── Target state ───────────────────────────────────────────────────────────────

/**
 * The slice a bound per-target run evaluates `since scope: "target"` against:
 * the pipeline-run chain, the coverage group, the location child, or the month
 * the run is about. Loaded only by the run endpoint, so property surfaces that
 * evaluate client-side simply see `target: null`.
 */
export interface WorkflowTargetState {
	events: WorkflowEvents;
	pipeline?: { latestScore: number | null; revisionCount: number };
}

// ── Combined ───────────────────────────────────────────────────────────────────

export interface WorkflowState {
	organization: WorkflowOrganizationState;
	property: WorkflowPropertyState | null;
	target: WorkflowTargetState | null;
}

// ── Key unions ─────────────────────────────────────────────────────────────────

export type PropertyCountKey =
	| "completedReportCount"
	| "reportRecipientCount"
	| "activePromptCount"
	| "promptStageCount"
	| "brandPromptCount"
	| "discoveryQueryCount"
	| "competitorCount"
	| "pageCount"
	| "pageGroupCount"
	| "activeProjectCount"
	| "activeProjectGoalCount"
	| "completedProjectCount"
	| "manualAnnotationCount"
	| "coverageGroupCount"
	| "coverageGroupsWithScoredPlacements"
	| "coverageGroupItemsWithPlacement"
	| "pitchListItemCount"
	| "faqGenerationCount"
	| "locationChildCount"
	| "snapshotOverrideFieldCount"
	| "aliasCount"
	| "additionalDomainCount"
	| "suppressedDomainCount"
	| "wrongDomainAlertRecipientCount"
	| "negativeMessageAlertRecipientCount"
	| "consultationCount"
	| "keyMessages.positive"
	| "keyMessages.negative"
	| "keyMessages.neutral"
	| "keyMessages.total"
	| "projectTypes.SEO"
	| "projectTypes.PR"
	| "projectTypes.GENERAL"
	| "goalTypes.PAGE_GROUP"
	| "goalTypes.PUBLISHER"
	| "goalTypes.ARTICLE"
	| "goalTypes.AUTHOR"
	| "goalTypes.COVERAGE_GROUP"
	| "goalTypes.PITCH_LIST"
	| "placements.prospective"
	| "placements.retrospective"
	| "placements.completed"
	| "pipeline.page"
	| "pipeline.url"
	| "pipeline.draft"
	| "pipeline.tracked"
	| "pipeline.completed"
	| "pipeline.rescored"
	| "audits.completedCount"
	| "trafficControl.customBlockCount"
	| "trafficControl.customAllowCount";

export type PropertyFlagKey =
	| "isVerified"
	| "hasAiReports"
	| "hasFaqGenerations"
	| "hasSnapshot"
	| "hasReportSchedule"
	| "hasNightlyMetrics"
	| "hasLocationMetrics"
	| "trafficControl.blockAiModelTrainers";

export type OrgCountKey =
	| "propertyCount"
	| "prospectCount"
	| "prospectReportsCompleteCount"
	| "prospectReportsExportedCount";

export type OrgFlagKey = "canProspect" | "hasWhiteLabelBranding";

// ── Readers ────────────────────────────────────────────────────────────────────

/**
 * Read a dotted key off a plain object. Returns `undefined` rather than
 * throwing when any segment is missing, so a state shape that predates a
 * catalog addition degrades to "not done" instead of crashing the page.
 */
export function readPath(source: unknown, key: string): unknown {
	let cursor: unknown = source;
	for (const segment of key.split(".")) {
		if (cursor === null || typeof cursor !== "object") {
			return undefined;
		}
		cursor = (cursor as Record<string, unknown>)[segment];
	}
	return cursor;
}

export function readPropertyCount(
	state: WorkflowPropertyState | null,
	key: PropertyCountKey,
): number {
	if (!state) {
		return 0;
	}
	const value = readPath(state, key);
	return typeof value === "number" ? value : 0;
}

export function readPropertyFlag(
	state: WorkflowPropertyState | null,
	key: PropertyFlagKey,
): boolean {
	if (!state) {
		return false;
	}
	return readPath(state, key) === true;
}

export function readOrgCount(
	state: WorkflowOrganizationState,
	key: OrgCountKey,
): number {
	const value = readPath(state, key);
	return typeof value === "number" ? value : 0;
}

export function readOrgFlag(
	state: WorkflowOrganizationState,
	key: OrgFlagKey,
): boolean {
	return readPath(state, key) === true;
}

// ── Plan predicate ─────────────────────────────────────────────────────────────

/**
 * Bulk prospecting is available on an active Agency plan that is not a trial.
 * Lifted verbatim out of `packages/api/src/routes/prospects/router.ts` (and
 * mirrored by `PropertiesDashboard.tsx`) so the workflow catalog gates W4 on
 * exactly the predicate the API enforces. Structurally typed on purpose: it
 * takes a `PlanInfo`-shaped object rather than importing `@repo/payments`,
 * which keeps this module out of the server-only graph.
 */
export function canProspectFromPlan(
	plan: {
		isAgency: boolean;
		hasActiveSubscription: boolean;
		isTrial: boolean;
	} | null,
): boolean {
	if (!plan) {
		return false;
	}
	return plan.isAgency && plan.hasActiveSubscription && !plan.isTrial;
}
